import { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  useLocation,
} from "react-router-dom";
import { Layout } from "./components/Layout";
import { NobodySausageGlyphProvider } from "./contexts/NobodySausageGlyphContext";
import { ThirdwebProviderWrapper } from "./contexts/ThirdwebProvider";

import { HowToClaim } from "./pages/HowToClaim";
import { Support } from "./pages/Support";
import { ClaimAdditional } from "./pages/ClaimAdditional";
import { NfcSimulator } from "./pages/NfcSimulator";
import { TicTacToe } from "./pages/TicTacToe";
import { MoleGame } from "./pages/MoleGame";
import { ChimpersWallpaper } from "./pages/ChimpersWallpaper";
import { CatGameWallpaper } from "./pages/CatGameWallpaper";
import { ClaimChimpers } from "./pages/ClaimChimpers";
import { ChimpersReward } from "./pages/ChimpersReward";
import { ClaimPonke } from "./pages/ClaimPonke";
import { ClaimRipndip } from "./pages/ClaimRipndip";
import { ClaimCatGame } from "./pages/ClaimCatGame";
import { ClaimExpoDemo } from "./pages/ClaimExpoDemo";
import { ClaimMew } from "./pages/ClaimMew";
import { ClaimNobodySausage } from "./pages/ClaimNobodySausage";
import Home from "./pages/Home";
import Chimpers from "./pages/Chimpers";
import Ponke from "./pages/Ponke";
import Ripndip from "./pages/Ripndip";
import CatGame from "./pages/CatGame";
import { CatGameReward } from "./pages/CatGameReward";
import ExpoDemo from "./pages/ExpoDemo";
import Mew from "./pages/Mew";
import NobodySausage from "./pages/NobodySausage";
import Teddymoo from "./pages/Teddymoo";
import { ClaimBkfc } from "./pages/ClaimBkfc";
import BkfcCertificates from "./pages/BkfcCertificates";
import BkfcRaffle from "./pages/BkfcRaffle";
import { BkfcAptosProvider } from "./contexts/BkfcAptosProvider";

// Shared layout for all NobodySausage routes. Mounting the Glyph provider on a
// parent route (rendered around <Outlet />) keeps a single provider instance
// alive while navigating between the claim and rewards pages, so the wagmi /
// Glyph / bridged-Thirdweb wallet state is never torn down between them.
function NobodySausageRoutesLayout() {
  return (
    <NobodySausageGlyphProvider>
      <Outlet />
    </NobodySausageGlyphProvider>
  );
}

// Shared layout for all BKFC routes. BKFC is the only Aptos flow in the app —
// mounting the Aptos wallet provider on a parent route (rendered around
// <Outlet />) keeps one wallet-adapter instance alive across the claim,
// rewards, certificates and raffle pages while leaving every EVM flow
// untouched. Same pattern as NobodySausageRoutesLayout above.
function BkfcRoutesLayout() {
  return (
    <BkfcAptosProvider>
      <Outlet />
    </BkfcAptosProvider>
  );
}

// React Router keeps the window scroll position across navigations; with the
// sticky header that makes a freshly opened page appear cut off at the top.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function NotFound() {
  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-50">
      <div className="mx-auto max-w-md text-center">
        <h1 className="mb-4 text-2xl font-bold text-gray-900">
          Page Not Found
        </h1>

        <p className="mb-8 text-gray-600">
          The page you're looking for doesn't exist.
        </p>

        <a
          href="/"
          className="px-6 py-3 text-white bg-black rounded-lg transition-colors hover:bg-gray-800"
        >
          Go Home
        </a>
      </div>
    </div>
  );
}

function App() {
  return (
    <ThirdwebProviderWrapper>
      <Router future={{ v7_startTransition: true }}>
        <ScrollToTop />
        <Routes>
          <Route
            path="/"
            element={
              <Layout>
                <Home />
              </Layout>
            }
          />
          <Route
            path="/claim/chimpers"
            element={
              <Layout footerLogo="chimpers">
                <ClaimChimpers />
              </Layout>
            }
          />
          <Route
            path="/claim/ponke"
            element={
              <Layout footerLogo="ponke">
                <ClaimPonke />
              </Layout>
            }
          />
          <Route
            path="/claim/ripndip"
            element={
              <Layout footerLogo="ripndip">
                <ClaimRipndip />
              </Layout>
            }
          />
          <Route
            path="/claim/catgame"
            element={
              <Layout footerLogo="catgame">
                <ClaimCatGame />
              </Layout>
            }
          />
          <Route
            path="/claim/expodemo"
            element={
              <Layout>
                <ClaimExpoDemo />
              </Layout>
            }
          />
          <Route
            path="/claim/mew"
            element={
              <Layout footerLogo="mew">
                <ClaimMew />
              </Layout>
            }
          />
          <Route element={<BkfcRoutesLayout />}>
            <Route
              path="/claim/bkfc"
              element={
                <Layout footerLogo="bkfc">
                  <ClaimBkfc />
                </Layout>
              }
            />
            {/* The certificates page IS the BKFC rewards surface (the old
                "Spin the wheel" screen was removed from the Figma flow);
                /rewards/bkfc/certificates stays as an alias because emailed
                links point there. */}
            <Route
              path="/rewards/bkfc"
              element={
                <Layout footerLogo="bkfc">
                  <BkfcCertificates />
                </Layout>
              }
            />
            <Route
              path="/rewards/bkfc/certificates"
              element={
                <Layout footerLogo="bkfc">
                  <BkfcCertificates />
                </Layout>
              }
            />
            <Route
              path="/rewards/bkfc/raffle"
              element={
                <Layout footerLogo="bkfc">
                  <BkfcRaffle />
                </Layout>
              }
            />
          </Route>
          <Route
            path="/claim/teddymoo"
            element={
              <Layout footerLogo="teddymoo">
                <Teddymoo />
              </Layout>
            }
          />
          <Route
            path="/claim/mewproject"
            element={
              <Layout footerLogo="mew">
                <ClaimMew />
              </Layout>
            }
          />
          <Route element={<NobodySausageRoutesLayout />}>
            <Route
              path="/claim/nobodysausage"
              element={
                <Layout>
                  <ClaimNobodySausage />
                </Layout>
              }
            />
            <Route
              path="/rewards/nobodysausage"
              element={
                <Layout>
                  <NobodySausage />
                </Layout>
              }
            />
          </Route>
          <Route
            path="/how-to-claim"
            element={
              <Layout>
                <HowToClaim />
              </Layout>
            }
          />
          <Route
            path="/support"
            element={
              <Layout>
                <Support />
              </Layout>
            }
          />
          <Route
            path="/rewards"
            element={
              <>
                <Layout>
                  <ClaimAdditional />
                </Layout>
              </>
            }
          />
          <Route
            path="/rewards/chimpers"
            element={
              <>
                <Layout footerLogo="chimpers">
                  <Chimpers />
                </Layout>
              </>
            }
          />
          <Route
            path="/rewards/ponke"
            element={
              <>
                <Layout footerLogo="ponke">
                  <Ponke />
                </Layout>
              </>
            }
          />
          <Route
            path="/rewards/ripndip"
            element={
              <>
                <Layout footerLogo="ripndip">
                  <Ripndip />
                </Layout>
              </>
            }
          />
          <Route
            path="/rewards/catgame"
            element={
              <>
                <Layout footerLogo="catgame">
                  <CatGame />
                </Layout>
              </>
            }
          />
          <Route
            path="/rewards/expodemo"
            element={
              <>
                <Layout>
                  <ExpoDemo />
                </Layout>
              </>
            }
          />
          <Route
            path="/rewards/mew"
            element={
              <>
                <Layout footerLogo="mew">
                  <Mew />
                </Layout>
              </>
            }
          />
          <Route
            path="/rewards/mewproject"
            element={
              <>
                <Layout footerLogo="mew">
                  <Mew />
                </Layout>
              </>
            }
          />
          <Route
            path="/chimpers-wallpaper"
            element={
              <Layout footerLogo="chimpers">
                <ChimpersWallpaper />
              </Layout>
            }
          />
          <Route
            path="/catgame-wallpaper"
            element={
              <Layout footerLogo="catgame">
                <CatGameWallpaper />
              </Layout>
            }
          />
          <Route
            path="/chimpers-reward"
            element={
              <Layout footerLogo="chimpers">
                <ChimpersReward />
              </Layout>
            }
          />
          <Route
            path="/catgame-reward"
            element={
              <Layout footerLogo="catgame">
                <CatGameReward />
              </Layout>
            }
          />
          {process.env.REACT_APP_NODE_ENV !== "production" && (
            <Route path="/simulator" element={<NfcSimulator />} />
          )}
          <Route path="/tictactoe" element={<TicTacToe />} />
          <Route path="/mole" element={<MoleGame />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </ThirdwebProviderWrapper>
  );
}

export default App;
